import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardRow from '@/domains/dashboard-management/components/CardRow';
import CardTotal from '@/domains/dashboard-management/components/CardTotal/CardTotal';
import WithdrawalHistory from '@/domains/dashboard-management/components/WithdrawalHistory/WithdrawalHistory';
import {
  useAdminAffiliateSummary,
  useAdminAffiliates,
} from '@/domains/dashboard-management/hooks/useAdminAffiliates';
import { STATUS_COURIER } from '@/domains/mobile-web/constant/listStatusCourier.constant';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Pagination from '@/shared/components/ui/Pagination/Pagination';
import { useColorStore } from '@/store/colorStore';

const PAGE_SIZE = 10;
const SEARCH_DELAY_MS = 300;

const rupiah = (value: string | number) => `Rp ${formatPrice(Number(value))}`;

const AffiliateManagement = () => {
  const navigate = useNavigate();
  const { NEWCOLOR } = useColorStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The list is asked for once typing pauses; a new keyword starts at page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setCurrentPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: summary } = useAdminAffiliateSummary();
  const { data: page, isLoading } = useAdminAffiliates({
    page: currentPage,
    size: PAGE_SIZE,
    q: query,
  });

  const rows = page?.rows ?? [];
  const totalPages = Math.max(1, page?.total_pages ?? 1);
  // Suspended and closed accounts are marked; active ones need no badge
  const inactive = STATUS_COURIER.Unavailable;

  return (
    <section className="flex! flex-col gap-6 overflow-auto mb-10">
      <h1 className="capitalize title2medium">Affiliate</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 md:gap-6 gap-3">
        <CardTotal
          title="Total Anggota Affiliate"
          total={summary?.total_members ?? '-'}
          unit="Anggota"
        />
        <CardTotal title="Total Klik Link" total={summary?.total_clicks ?? '-'} unit="Klik" />
        <CardTotal
          title="Total Transaksi"
          total={summary?.total_transactions ?? '-'}
          unit="Transaksi"
        />
        <CardTotal
          title="Total Pembelian"
          total={summary ? rupiah(summary.total_purchases) : '-'}
        />
      </div>

      <div className="shadowCard flex flex-col gap-3">
        <div className="w-full p-3 md:p-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="shrink-0">
            <p className="title1bold capitalize">Daftar Anggota</p>
            <p className="body1 text-text-grey">{page?.total ?? 0} anggota ditemukan</p>
          </div>
          <div className="flex flex-col   gap-3 w-full sm:flex-row md:flex-1 md:justify-end md:max-w-[400px]">
            <DetailForm
              inputType="text"
              placeholder="ID Anggota, Nomor Invoice atau Status"
              showTitle={false}
              inputFormStyle="store"
              className="w-full sm:flex-1 sm:min-w-0"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />
        <div className="flex-col flex gap-6 p-3 md:p-6 w-full overflow-auto min-h-[200px] max-h-[500px]">
          {rows.length > 0 ? (
            rows.map((member) => (
              <CardRow
                key={member.affiliate_id}
                code={member.affiliate_id}
                badge={
                  member.status === 'active'
                    ? undefined
                    : {
                        label: member.status_label ?? member.status,
                        bg: NEWCOLOR[inactive.bg],
                        color: inactive.color,
                      }
                }
                subtitle={member.name ?? '-'}
                fields={[
                  { key: 'clicks', label: 'TOTAL KLIK LINK', value: member.click_total },
                  {
                    key: 'pending',
                    label: 'KOMISI BELUM DIBAYARKAN',
                    value: rupiah(member.balance),
                  },
                  {
                    key: 'commission',
                    label: 'TOTAL KOMISI DIDAPAT',
                    value: rupiah(member.commission_total),
                  },
                ]}
                actions={[
                  {
                    key: 'detail',
                    label: 'Detail',
                    variant: 'outline',
                    onClick: () =>
                      navigate(`/dashboard/affiliate-detail?id=${member.affiliate_id}`),
                  },
                ]}
                className={expandedId === member.affiliate_id ? 'border-[#007AFF]' : ''}
                styleArrow={expandedId === member.affiliate_id ? 'rotate-180' : ''}
                onAcction={() =>
                  setExpandedId((id) => (id === member.affiliate_id ? null : member.affiliate_id))
                }
              >
                {expandedId === member.affiliate_id && (
                  <WithdrawalHistory affiliateId={member.affiliate_id} />
                )}
              </CardRow>
            ))
          ) : (
            <p className="text-center">{isLoading ? 'Memuat...' : 'Tidak ada data'}</p>
          )}
        </div>

        <div className="divider h-[0.5px] w-full bg-blue-cs-200" />
        <div className="w-full p-3 md:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="body1 text-text-grey whitespace-nowrap">
            Menampilkan <span className="font-bold text-text-black">{PAGE_SIZE}</span> Data per
            Halaman
          </p>
          <div className="flex  items-center gap-[6px] justify-center m-auto lg:m-0">
            <Pagination
              pageCount={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              previousLabel={<ChevronLeft size={16} />}
              nextLabel={<ChevronRight size={16} />}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AffiliateManagement;
